import React, { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { SquareLoader } from "../Main/components/Loader/SquareLoader";
import InfiniteScroll from "react-infinite-scroll-component";
import { motion } from "framer-motion";
import { useNewsStore } from "./store";
import { newsApi } from "./service";
import { NewItemCard } from "./components/NewItemCard";
import { NewsEditForm } from "./components/NewsEditForm";
import { AddNewForm } from "./components/AddNewForm";
import { DeleteModal } from "screens/DApps/components/DeleteModal";
import clsx from "clsx";

interface PropsTypes {}

export const NewsPage = ({}: PropsTypes) => {
  const {
    news,
    addNews,
    setNews,
    newToEdit,
    setNewEdit,
    page,
    setPage,
    setHasMore,
    hasMore,
    isAddLoading,
  } = useNewsStore();
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [totalNewsItems, setTotalNewsItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string>("");

  useEffect(() => {
    setIsLoading(true);
    const loadInitialData = async () => {
      try {
        const { data, meta } = await newsApi.getNews(page);
        if (data.length === 0) {
          setHasMore(false);
        }
        setTotalNewsItems(meta.totalItems);

        setTotalPages(meta.totalPages);
        const isThereMore = meta.totalPages !== page;
        setHasMore(isThereMore);
        setPage(page);
        setNews(data);
      } catch {
        console.error("Error fetching data");
      } finally {
        setIsLoading(false);
      }
    };
    loadInitialData();
    // eslint-disable-next-line
  }, []);

  const loadMore = async () => {
    if (!hasMore) return;
    if (totalPages < page + 1) return;

    setIsLoadMoreLoading(true);

    const nextPage = page + 1;

    try {
      const { data, meta } = await newsApi.getNews(nextPage);
      addNews(data);
      const isThereMore = nextPage < meta.totalPages;

      setHasMore(isThereMore);
      setPage(isThereMore ? nextPage : page);
    } catch (error) {
      console.error("Error loading data", error);
    } finally {
      setIsLoadMoreLoading(false);
    }
  };

  useEffect(() => {
    if (isAddFormOpen || !!newToEdit) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isAddFormOpen, newToEdit]);

  const onDelete = () => {
    newsApi.deleteNew({ newId: idToDelete });
    setIsDeleteModalOpen(false);
  };

  return (
    <div className="px-[14px] sm:px-[154px] flex flex-col items-center">
      <h1 className="mb-[50px] text-[24px] text-center px-[50px] sm:px-0 md:text-[36px] font-[700]">
        News Admin Panel
      </h1>

      <Button
        extraClass="flex items-center justify-center mb-[60px] min-w-[192px]"
        onClick={() => setIsAddFormOpen(true)}
      >
        <span className="mr-[10px] text-[18px]">+</span> Add new
      </Button>

      <div className="w-full">
        <p className="font-[700] mb-[24px]">All news ({totalNewsItems})</p>
        {isLoading ? (
          <div className="flex w-full  my-[20px] justify-center  items-center">
            <SquareLoader size={32} />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={news.length}
            next={() => loadMore()}
            hasMore={hasMore}
            loader={<p></p>}
            endMessage={<p></p>}
          >
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-7 w-full mb-16">
              {news.length ? (
                news?.map((newItem, i) => (
                  <motion.article
                    initial={{
                      y: 50,
                      opacity: 0,
                    }}
                    whileInView={{
                      y: 0,
                      opacity: 1,
                    }}
                    transition={{
                      duration: 0.1,
                      delay: 0.1 * (i % 3),
                    }}
                    viewport={{
                      once: true,
                      margin: "-20% 0%",
                    }}
                    key={newItem.id || `${newItem.title}-${i}`}
                    className="relative flex flex-col gap-[8px] min-h-[251px] py-[22px] pl-[19px] pr-[24px] bg-[#171717]"
                  >
                    <NewItemCard
                      setIsDeleteModalOpen={setIsDeleteModalOpen}
                      setIdToDelete={setIdToDelete}
                      newItem={newItem}
                    />
                  </motion.article>
                ))
              ) : (
                <p className="W-full flex items-center justify-center mt-[50px]">
                  No news available
                </p>
              )}
            </section>
          </InfiniteScroll>
        )}
        {hasMore &&
          !isLoading &&
          (isLoadMoreLoading ? (
            <div className="flex w-full  my-[20px] justify-center  items-center">
              <SquareLoader size={32} />
            </div>
          ) : (
            <div className="w-full flex justify-center">
              <button className="text-white hover:underline" onClick={loadMore}>
                Load more...
              </button>
            </div>
          ))}
      </div>

      <AddNewForm
        isOpen={isAddFormOpen}
        onClose={() => setIsAddFormOpen(false)}
      />

      <div
        className={clsx(
          "fixed inset-0  items-center justify-center z-50",
          isDeleteModalOpen ? "flex" : "hidden"
        )}
      >
        <DeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={onDelete}
          title="new"
        />
      </div>

      {!!newToEdit && (
        <NewsEditForm
          newToEdit={newToEdit}
          isOpen={!!newToEdit}
          onClose={() => setNewEdit(null)}
        />
      )}
    </div>
  );
};
